/* ----------------------------------------------------------------------------
autoplay for the showcase block (_includes/blocks/showcase.html):
<feature-showcase autoplay="6">…</feature-showcase> opens the next item when
the open one's progress bar fills. It stops for good once someone opens an
item, pauses with its button or off screen, and never plays with reduced
motion. Without it, the showcase is a plain one-open-at-a-time accordion.
---------------------------------------------------------------------------- */

export default class FeatureShowcase extends HTMLElement {
	constructor() {
		super();
		this.userPaused = false;
		this.offscreen = false;
		this.handleClick = this.handleClick.bind(this);
		this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
	}

	get items() {
		return [...this.querySelectorAll('.showcase__items > details')];
	}

	connectedCallback() {
		this.wide = window.matchMedia('(width >= 50em)');
		this.addEventListener('click', this.handleClick);

		// once it's near the viewport, load every picture, so switching never waits
		this.observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					for (const img of this.querySelectorAll('img')) img.loading = 'eager';
				}
				this.offscreen = !entry.isIntersecting;
				this.updatePaused();
			},
			{ rootMargin: '200px' },
		);
		this.observer.observe(this);

		const seconds = Number(this.getAttribute('autoplay'));
		const reducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches;
		if (seconds > 0 && !reducedMotion && this.items.length > 1) {
			this.play(seconds);
		}
	}

	disconnectedCallback() {
		this.observer?.disconnect();
		this.removeEventListener('click', this.handleClick);
		this.removeEventListener('animationend', this.handleAnimationEnd);
	}

	play(seconds) {
		this.style.setProperty('--showcase-interval', `${seconds}s`);
		this.setAttribute('playing', '');
		this.addEventListener('animationend', this.handleAnimationEnd);

		// moving content needs a way to stop it (WCAG 2.2.2)
		this.pauseButton = document.createElement('button');
		this.pauseButton.type = 'button';
		this.pauseButton.className = 'showcase__pause button --simple --primary';
		this.pauseButton.textContent = 'Pause';
		this.pauseButton.addEventListener('click', () => {
			this.userPaused = !this.userPaused;
			this.pauseButton.textContent = this.userPaused ? 'Play' : 'Pause';
			this.updatePaused();
		});
		this.append(this.pauseButton);
	}

	stop() {
		this.removeAttribute('playing');
		this.removeAttribute('paused');
		this.removeEventListener('animationend', this.handleAnimationEnd);
		this.pauseButton?.remove();
	}

	updatePaused() {
		this.toggleAttribute('paused', this.userPaused || this.offscreen);
	}

	handleAnimationEnd(e) {
		if (e.animationName !== 'showcase-progress') return;
		const items = this.items;
		const open = items.findIndex((item) => item.open);
		items[(open + 1) % items.length].open = true;
	}

	handleClick(e) {
		const summary = e.target.closest('summary');
		if (!summary || !this.contains(summary)) return;

		// side by side, one item stays open so the picture never disappears
		if (summary.parentElement.open && this.wide.matches) e.preventDefault();

		// someone chose an item: stop playing for good
		if (this.hasAttribute('playing')) this.stop();
	}
}

window.customElements.define('feature-showcase', FeatureShowcase);
